/**
 * UserBlockService — symmetric user blocking (Twitter / Instagram pattern).
 *
 * The DB layer (RLS + RPC bodies in migration 20260429140000) does the
 * heavy lifting: once a row exists in `user_blocks`, the blocker can no
 * longer see the blocked user's vehicles in Explore, can't open their
 * profile, can't message them, and can't book them. Same in the other
 * direction. This service is just the typed wrapper around that table.
 *
 * Mutations invalidate the relevant TanStack caches in the consuming hook
 * (lib/hooks/use-user-blocks.ts) so blocked content disappears from every
 * screen automatically without per-screen plumbing.
 */
import { supabase } from '../supabase'
import { ServiceError } from '../errors/service-error'

export interface UserBlockRow {
  id: string
  blockedId: string
  /** Display name of the blocked user (joined from profiles via SECURITY DEFINER RPC). */
  displayName: string | null
  /** Avatar URL of the blocked user. */
  avatarUrl: string | null
  reason: string | null
  createdAt: string
}

class UserBlockServiceImpl {
  /**
   * Block another user. Idempotent — a duplicate block (same blocker +
   * blocked pair) just no-ops. The reason is optional; if provided it's
   * surfaced to admin/support for moderation context.
   */
  async block(blockerId: string, blockedId: string, reason?: string): Promise<void> {
    if (blockerId === blockedId) {
      throw new ServiceError({
        message: 'Cannot block yourself',
        code: 'BLOCK_SELF',
        statusCode: 400,
      })
    }

    const { error } = await supabase.from('user_blocks').upsert(
      {
        blocker_id: blockerId,
        blocked_id: blockedId,
        reason: reason ?? null,
      },
      { onConflict: 'blocker_id,blocked_id', ignoreDuplicates: true },
    )

    if (error) throw ServiceError.fromSupabaseError(error)
  }

  /**
   * Unblock — removes the row by composite key. Idempotent — unblocking
   * a non-blocked user no-ops cleanly.
   */
  async unblock(blockerId: string, blockedId: string): Promise<void> {
    const { error } = await supabase
      .from('user_blocks')
      .delete()
      .eq('blocker_id', blockerId)
      .eq('blocked_id', blockedId)

    if (error) throw ServiceError.fromSupabaseError(error)
  }

  /**
   * List every user the current user has blocked, joined with the public
   * profile fields needed by the management screen. Calls the
   * `get_blocked_profiles_for_viewer()` RPC (SECURITY DEFINER, scoped to
   * caller via `auth.uid()`) so the blocker-hides-blocked RLS doesn't
   * also hide the user's own block list from themselves.
   */
  async listBlocked(_blockerId: string): Promise<UserBlockRow[]> {
    const { data, error } = await supabase.rpc('get_blocked_profiles_for_viewer')
    if (error) throw ServiceError.fromSupabaseError(error)
    if (!data) return []
    return (
      data as Array<{
        block_id: string
        blocked_id: string
        display_name: string | null
        avatar_url: string | null
        reason: string | null
        created_at: string
      }>
    ).map((row) => ({
      id: row.block_id,
      blockedId: row.blocked_id,
      displayName: row.display_name,
      avatarUrl: row.avatar_url,
      reason: row.reason,
      createdAt: row.created_at,
    }))
  }

  /**
   * Symmetric block check via the `is_user_blocked` SQL function.
   * Returns true if EITHER side has blocked the other; used to gate
   * actions like booking creation as defense-in-depth alongside RLS.
   */
  async isBlocked(viewerId: string, targetId: string): Promise<boolean> {
    if (viewerId === targetId) return false
    const { data, error } = await supabase.rpc('is_user_blocked', {
      viewer_id: viewerId,
      target_id: targetId,
    })

    if (error) throw ServiceError.fromSupabaseError(error)
    return Boolean(data)
  }
}

export const userBlockService = new UserBlockServiceImpl()
